import _ from 'lodash';
import moment from 'moment';
import { all, apply, call, put, select, takeEvery } from 'redux-saga/effects';
import Cookies from 'universal-cookie';
import appHistory, { excludeSearchParams, isExistSearchParam } from '../../app/appHistory';
import {
    getAppModeSelector,
    getAppUser,
    getUserSelectedAccountCode,
    getUserSelectedAccountType,
    getUserUidSelector,
} from '../../app/appSelectors';
import { failurePut } from '../../app/failurePut';
import { deleteRequest, getRequest, postRequest } from '../../app/request';
import { ORDER_TYPES } from '../../components/OrderTypeSelector/orderTypeSelectorConstants';
import parseAccountName from '../../utils/parseAccountName';
import Asset from '../Asset/Asset';
import { logoutAction } from '../Login/loginActions';
import { openNotificationAction, sendNotificationMessageAction } from '../Notifications/notificationsReducer';
import LiteTradeNotificationBasicInfo from './components/LiteTradeNotification/LiteTradeNotificationBasicInfo';
import LiteTradeNotificationCloseButton from './components/LiteTradeNotification/LiteTradeNotificationCloseButton';
import {
    LITE_TRADE_NOTIFICATION_TYPES,
    MAP_LITE_TRADE_ORDER_STATUS_TO_ORDER_NOTIFICATION_TYPE,
} from './components/LiteTradeNotification/liteTradeNotificationConstants';
import LiteTradeNotificationPositionClosed
    from './components/LiteTradeNotification/LiteTradeNotificationPositionClosed';
import LiteTradeNotificationProtectionOrderInfo
    from './components/LiteTradeNotification/LiteTradeNotificationProtectionOrderInfo';
import {
    cancelLiteTradeOrderFailure,
    cancelLiteTradeOrderSuccess,
    closeAllPositionsAction,
    closeAllPositionsFailure,
    closeAllPositionsSuccess,
    closeLiteTradePositionAction,
    closeLiteTradePositionFailure,
    closeLiteTradePositionSuccess,
    createLiteTradeOrderFailure,
    createLiteTradeOrderSuccess,
    getLiteTradeAccountStatPLFailure,
    getLiteTradeAccountStatPLSuccess,
    getLiteTradeAssetsFailure,
    getLiteTradeAssetsSuccess,
    getLiteTradeMarketCollectionAction,
    getLiteTradeMarketCollectionFailure,
    getLiteTradeMarketCollectionSuccess,
    getLiteTradeMarketFailure,
    getLiteTradeMarketSuccess,
    ICancelLiteTradeOrderSuccessPayload,
    ICloseLiteTradePositionSuccessPayload,
    ICreateLiteTradeOrderSuccessPayload,
    initChartDrawPanelOpenedAction,
    initLiteTradeFailure,
    initLiteTradeSuccess,
    initUpsDownsCollectionAction,
    initUpsDownsCollectionFailure,
    initUpsDownsCollectionSuccess,
    IOpenLiteTradePositionSuccessPayload,
    IUpdateLiteTradeOrderSuccessPayload,
    loginLiteTradeAction,
    loginLiteTradeFailure,
    loginLiteTradeSuccess,
    logoutLiteTradeFailure,
    logoutLiteTradeSuccess,
    openLiteTradePositionAction,
    openLiteTradePositionFailure,
    openLiteTradePositionSuccess,
    pingLiteTradeAction,
    pingLiteTradeFailure,
    pingLiteTradeSuccess,
    processLiteTradePositionNotificationAction,
    setChartDrawPanelOpenedAction,
    updateLiteTradeMarketFavoriteAssetFailure,
    updateLiteTradeMarketFavoriteAssetSuccess,
    updateLiteTradeOrderAction,
    updateLiteTradeOrderFailure,
    updateLiteTradeOrderSuccess,
    updateLiteTradePositionFailure,
    updateLiteTradePositionSuccess,
    updateLiteTradeViewAssetFailure,
    updatePositionPlLastSeenSignByPortfolioAction,
    updatePositionPlLastSeenSignByPortfolioSuccess,
    updatePositionPlLastSeenSignByPositionCodeAction,
} from './liteTradeActions';
import {
    CANCEL_LITE_TRADE_ORDER_ACTION,
    CLOSE_ALL_POSITIONS_ACTION,
    CLOSE_LITE_TRADE_POSITION_ACTION,
    CREATE_LITE_TRADE_ORDER_ACTION,
    GET_LITE_TRADE_ACCOUNT_STAT_PL_ACTION,
    GET_LITE_TRADE_ASSETS_ACTION,
    GET_LITE_TRADE_MARKET_ACTION,
    GET_LITE_TRADE_MARKET_COLLECTION_ACTION,
    INIT_CHART_DRAW_PANEL_OPENED,
    INIT_LITE_TRADE_ACTION,
    INIT_UPS_DOWNS_COLLECTION_ACTION,
    LITE_TRADE_ORDER_STATUSES,
    LITE_TRADE_POSITION_EFFECTS,
    LITE_TRADE_POSITION_SIDE,
    LOGIN_LITE_TRADE_ACTION,
    LOGOUT_LITE_TRADE_ACTION,
    MAP_LITE_TRADE_POSITION_OPPOSITE_SIDE,
    OPEN_LITE_TRADE_POSITION_ACTION,
    PING_LITE_TRADE_ACTION,
    PROCESS_LITE_TRADE_ORDER_NOTIFICATION,
    PROCESS_LITE_TRADE_POSITION_NOTIFICATION,
    PROCESS_LITE_TRADE_REJECTED_ORDER_NOTIFICATION,
    UPDATE_LITE_TRADE_MARKET_FAVORITE_ASSET_ACTION,
    UPDATE_LITE_TRADE_MARKET_VIEW_ASSET_ACTION,
    UPDATE_LITE_TRADE_ORDER_ACTION,
    UPDATE_LITE_TRADE_POSITION_ACTION,
    UPDATE_POSITION_PL_LAST_SEEN_SIGN_BY_PORTFOLIO_ACTION,
    UPDATE_POSITION_PL_LAST_SEEN_SIGN_BY_POSITION_CODE_ACTION,
} from './liteTradeConstants';
import {
    deleteLiteTradeRequest,
    getLiteTradeRequest,
    postLiteTradeRequest,
    putLiteTradeRequest,
} from './liteTradeRequest';
import {
    getCloseAllPositionsIsLoading,
    getLiteTradeAssets,
    makeSelectorGetLiteTradeAccountOrdersByAccountCode,
} from './liteTradeSelectors';
import { COLLECTION_KEYS } from '../../mobileAppCore/components/MobileMarket/mobileMarketConstants';
import type {
    ILiteTradeAsset,
    ILiteTradeAssetInfo,
    ILiteTradeAssetMarket,
    ILiteTradeAssets,
    ILiteTradeAssetSymbol,
    ILiteTradeConfig,
    ILiteTradePortfolioPosition,
    ILiteTradePortfolioPositionTift,
    ILiteTradePortfolioPositionType,
    ILiteTradeSession,
    IOrderCode,
    IPositionCode,
} from './types/liteTradeReducer.t';
import React from 'react';
import { IAccountCode } from '../../app/types/appReducer.t';
import { IAccountType } from '../../app/types/appActions.t';
import { MOBILE_SLIDER_PAGES_IDS } from '../../mobileAppCore/components/MobileSliderPages/mobileSliderPagesConstants';
import {
    NOTIFICATION_MESSAGE_MARKET_EVENTS,
    NOTIFICATIONS_MESSAGE_TYPES,
} from '../Notifications/notificationsConstants';
import { ACCOUNT_TYPES, LOCAL_STORAGE_KEYS } from '../../app/appConstants';
import { pushGtmEventAction } from '../GoogleTagManager/googleTagManagerActions';
import { GTM_EVENTS } from '../GoogleTagManager/googleTagManagerConstants';
import { TLiteTradeNotificationType } from './components/LiteTradeNotification/liteTradeNotification.t';
import { getIsLocationPlatformDesktopSelector, getLocationPlatformSelector } from '../Location/locationSelectors';
import classNames from 'classnames';
import { getLocalStorageItem } from '../../utils/storage/getLocalStorageItem';
import { setLocalStorageItem } from '../../utils/storage/setLocalStorageItem';
import LiteTradeNotificationCloseAll from './components/LiteTradeNotification/LiteTradeNotificationCloseAll';
import { TRADING_VIEW_STORAGE_KEY_CHART_DRAWING_TOOLBAR_VISIBLE } from './liteTradeReducer';
import { sendFacebookEventAction } from "../../app/appActions";
import { FB_EVENTS } from "../../utils/sendFacebookEvent";

const browserCookies = new Cookies();

const arrayToObject = <T, N extends string>(array: T[], key: string): { [name in N]: T } => {
    return array.reduce((acc, item) => {
        acc[item[key]] = item;
        return acc;
    }, {} as { [name in N]: T });
};

export function* initLiteTradeSaga() {
    try {
        const data: ILiteTradeConfig = yield call<typeof getRequest>(getRequest, '/user/lite-trade/init');
        localStorage.setItem('apiTrade', data.tradeUrl);
        yield put(initLiteTradeSuccess(data));
        yield put(pingLiteTradeAction());
    } catch (e: any) {
        yield call<typeof failurePut>(failurePut, e, initLiteTradeFailure);
    }
}

export function* pingLiteTradeSaga() {
    try {
        const data: ILiteTradeSession = yield call<typeof postLiteTradeRequest>(postLiteTradeRequest, '/ping', null, null, {
            timeout: 5000,
        });

        const pin = browserCookies.get('pin');
        browserCookies.set('pinOk', pin, { path: '/', maxAge: 60 * 5 });

        yield put(pingLiteTradeSuccess(data));
    } catch (e: any) {
        if (e.response?.status === 401) {
            yield put(loginLiteTradeAction());
        } else {
            yield call(failurePut, e, pingLiteTradeFailure);
        }
    }
}

export function* loginLiteTradeSaga() {
    try {
        const data: ILiteTradeSession = yield call(postRequest, '/user/lite-trade/login');
        if (data?.sessionToken) {
            localStorage.setItem('sessionToken', data.sessionToken);
        }
        yield put(loginLiteTradeSuccess(data));
    } catch (e: any) {
        yield call(failurePut, e, loginLiteTradeFailure);
        yield put(logoutAction());
    }
}

export function* logoutLiteTradeSaga() {
    try {
        // const data = yield call(postLiteTradeRequest, '/logout');
        localStorage.removeItem('selectedAccount');
        localStorage.removeItem('sessionToken');
        yield put(logoutAction());
        yield put(logoutLiteTradeSuccess());
    } catch (e: any) {
        yield call<typeof failurePut>(failurePut, e, logoutLiteTradeFailure);
    }
}

export function* getLiteTradeAssetsSaga() {
    try {
        const selectedAccount: IAccountCode = (yield select(getUserSelectedAccountCode)) as IAccountCode;
        const data: {
            instrumentDetails: Omit<ILiteTradeAsset, 'info'>[]
        } = yield call<typeof getLiteTradeRequest>(getLiteTradeRequest, `/accounts/default:${encodeURIComponent(selectedAccount)}/instruments/query`);
        const info: {
            instruments: ILiteTradeAssetInfo[]
        } = yield call<typeof getLiteTradeRequest>(getLiteTradeRequest, `/instruments/query`);
        const market: ILiteTradeAssetMarket[] = yield  call<typeof getRequest>(getRequest, `/market/assets/list`);
        const infoObject = arrayToObject(info.instruments, 'symbol');
        const marketObject = arrayToObject(market, 'symbol');
        const assets: ILiteTradeAssets = data.instrumentDetails
            .filter(item => item.symbol !== 'BAT_TST')
            .map((item) => ({
                ...item,
                ...marketObject[item.symbol],
                info: infoObject[item.symbol],
            }));
        yield put(getLiteTradeAssetsSuccess(assets));
    } catch (e: any) {
        yield call(failurePut, e, getLiteTradeAssetsFailure);
    }
}

export function* getOrderCode() {
    const uid: string | null | undefined = yield select(getUserUidSelector);
    const accountType: IAccountType | undefined = yield select(getUserSelectedAccountType);
    const platform = yield select(getLocationPlatformSelector);
    const appMode = yield select(getAppModeSelector);
    return `source_${appMode}_${platform}-${accountType}_${uid}-${moment().unix()}`;
}

/**
 * @deprecated use openLiteTradePositionSaga/closeLiteTradePositionSaga
 */
export function* createLiteTradeOrderSaga({ payload }) {
    try {
        const selectedAccount: IAccountCode | undefined = yield select(getUserSelectedAccountCode);
        const orderCode: string = yield call<typeof getOrderCode>(getOrderCode);
        const data: ICreateLiteTradeOrderSuccessPayload = yield call<typeof postLiteTradeRequest>(postLiteTradeRequest, `/accounts/default:${encodeURIComponent(selectedAccount as string)}/orders`, {
            ...payload,
            orderCode,
        });
        yield put(createLiteTradeOrderSuccess(data));
        if (payload.positionEffect === 'OPEN') {
            appHistory.goBack();
        }
    } catch (e: any) {
        yield call(failurePut, e, createLiteTradeOrderFailure);
    }
}

export function* openLiteTradePositionSaga({ payload }: ReturnType<typeof openLiteTradePositionAction>) {
    try {
        const orders: ILiteTradePortfolioPosition[] = [];
        const selectedAccount: IAccountCode = yield select(getUserSelectedAccountCode);
        const orderCode: IOrderCode = yield call(getOrderCode);
        const params: ILiteTradePortfolioPosition = {
            instrument: payload.symbol,
            orderCode,
            positionEffect: LITE_TRADE_POSITION_EFFECTS.OPEN,
            quantity: payload.quantity,
            side: payload.side.toLowerCase() === 'buy' ? LITE_TRADE_POSITION_SIDE.BUY : LITE_TRADE_POSITION_SIDE.SELL,
            tif: 'GTC',
            type: payload.orderType,
            limitPrice: undefined,
            stopPrice: undefined,
        };
        switch (payload.orderType) {
            case ORDER_TYPES.LIMIT:
                params.limitPrice = payload.limitPrice;
                break;
            case ORDER_TYPES.STOP:
                params.stopPrice = payload.stopPrice;
                break;
            default:
        }
        orders.push(params);

        if (payload.takeProfitActive) {
            orders.push({
                instrument: payload.symbol,
                orderCode: `${orderCode}-take` as IOrderCode,
                positionEffect: LITE_TRADE_POSITION_EFFECTS.CLOSE,
                side: payload.side.toLowerCase() === 'sell' ?
                    LITE_TRADE_POSITION_SIDE.BUY :
                    LITE_TRADE_POSITION_SIDE.SELL,
                tif: 'GTC',
                type: ORDER_TYPES.LIMIT,
                limitPrice: payload.takeProfit,
            });
        }

        if (payload.stopLossActive) {
            orders.push({
                instrument: payload.symbol,
                orderCode: `${orderCode}-stop` as IOrderCode,
                positionEffect: LITE_TRADE_POSITION_EFFECTS.CLOSE,
                side: payload.side.toLowerCase() === 'sell' ?
                    LITE_TRADE_POSITION_SIDE.BUY :
                    LITE_TRADE_POSITION_SIDE.SELL,
                tif: 'GTC',
                type: ORDER_TYPES.STOP,
                stopPrice: payload.stopLoss,
            });
        }

        let body;
        if (orders.length > 1) {
            body = {
                orders,
                contingencyType: 'IF-THEN',
            };
        } else {
            body = orders[0];
        }
        const data: IOpenLiteTradePositionSuccessPayload = yield call<typeof postLiteTradeRequest>(postLiteTradeRequest, `/accounts/default:${encodeURIComponent(selectedAccount)}/orders`, body);
        yield put(openLiteTradePositionSuccess(data));

        const userProfile = yield select(getAppUser);
        yield put(sendFacebookEventAction({
            event: FB_EVENTS.TRADING_DEMO_DEAL,
            eventID: `position_open_${userProfile.uid}`,
            data: {},
        }));
        if (typeof payload.onSuccessCallback === 'function') {
            payload.onSuccessCallback();
        } else if (params.positionEffect === LITE_TRADE_POSITION_EFFECTS.OPEN) {
            appHistory.goBack();
        }
    } catch (e: any) {
        yield call(failurePut, e, openLiteTradePositionFailure);
    }
}


export function* closeLiteTradePositionSaga({ payload }: ReturnType<typeof closeLiteTradePositionAction>) {
    try {
        const selectedAccount: IAccountCode = yield select(getUserSelectedAccountCode);
        const orderCode: IOrderCode = yield call(getOrderCode);
        const params: ILiteTradePortfolioPosition = {
            instrument: payload.symbol,
            orderCode,
            positionCode: payload.positionCode as IPositionCode,
            positionEffect: LITE_TRADE_POSITION_EFFECTS.CLOSE,
            quantity: payload.quantity,
            side: payload.side,
            tif: 'GTC',
            type: ORDER_TYPES.MARKET,
        };

        const data: ICloseLiteTradePositionSuccessPayload = yield call(postLiteTradeRequest, `/accounts/default:${encodeURIComponent(selectedAccount)}/orders`, params);
        yield put(closeLiteTradePositionSuccess(data));
        payload.onSuccessCallback && payload.onSuccessCallback();
    } catch (e: any) {
        yield call(failurePut, e, closeLiteTradePositionFailure);
    }
}

interface ICloseLiteTradeOrderSagaPayload {
    payload: {
        orderCode: string;
        onSuccessCallback?: () => void;
    };
}

export function* cancelLiteTradeOrderSaga({ payload }: ICloseLiteTradeOrderSagaPayload) {
    try {
        const selectedAccount: IAccountCode = yield select(getUserSelectedAccountCode);
        const data: ICancelLiteTradeOrderSuccessPayload = yield call<typeof deleteLiteTradeRequest>(deleteLiteTradeRequest, `/accounts/default:${encodeURIComponent(selectedAccount)}/orders/${encodeURIComponent(payload.orderCode)}`);
        payload.onSuccessCallback && payload.onSuccessCallback();
        yield put(cancelLiteTradeOrderSuccess(data));
    } catch (e: any) {
        yield call(failurePut, e, cancelLiteTradeOrderFailure);
    }
}

export function* updateLiteTradeOrderSaga({ payload }: ReturnType<typeof updateLiteTradeOrderAction>) {
    try {
        const orders: ILiteTradePortfolioPosition[] = [];
        const selectedAccount: IAccountCode = yield select(getUserSelectedAccountCode);
        const params: ILiteTradePortfolioPosition = {
            instrument: payload.symbol,
            orderCode: payload.orderCode,
            positionEffect: LITE_TRADE_POSITION_EFFECTS.OPEN,
            quantity: payload.quantity,
            side: payload.side.toLowerCase() === 'buy' ? LITE_TRADE_POSITION_SIDE.BUY : LITE_TRADE_POSITION_SIDE.SELL,
            tif: 'GTC',
        };
        switch (payload.orderType) {
            case ORDER_TYPES.LIMIT:
                params.limitPrice = payload.limitPrice;
                break;
            case ORDER_TYPES.STOP:
                params.stopPrice = payload.stopPrice;
                break;
            default:
        }
        orders.push(params);

        if (payload.takeProfitActive) {
            orders.push({
                instrument: payload.symbol,
                orderCode: payload.initialOrderTakeProfit?.orderCode || `${payload.orderCode}-take-${moment().unix()}` as IOrderCode,
                positionEffect: LITE_TRADE_POSITION_EFFECTS.CLOSE,
                side: payload.side.toLowerCase() === 'sell' ?
                    LITE_TRADE_POSITION_SIDE.BUY :
                    LITE_TRADE_POSITION_SIDE.SELL,
                tif: 'GTC',
                limitPrice: payload.takeProfit,
            });
        }

        if (payload.stopLossActive) {
            orders.push({
                instrument: payload.symbol,
                orderCode: payload.initialOrderStopLoss?.orderCode || `${payload.orderCode}-stop-${moment().unix()}` as IOrderCode,
                positionEffect: LITE_TRADE_POSITION_EFFECTS.CLOSE,
                side: payload.side.toLowerCase() === 'sell' ?
                    LITE_TRADE_POSITION_SIDE.BUY :
                    LITE_TRADE_POSITION_SIDE.SELL,
                tif: 'GTC',
                type: ORDER_TYPES.STOP,
                stopPrice: payload.stopLoss,
            });
        }

        let body;
        if (orders.length > 1) {
            body = {
                orders,
                contingencyType: 'IF-THEN',
            };
        } else {
            body = orders[0];
        }
        const data: IUpdateLiteTradeOrderSuccessPayload = yield call(putLiteTradeRequest, `/accounts/default:${encodeURIComponent(selectedAccount)}/orders`, body);
        yield put(updateLiteTradeOrderSuccess(data));
        yield call(payload.onSuccess);
    } catch (e: any) {
        yield call(failurePut, e, updateLiteTradeOrderFailure);
    }
}

interface IUpdateLiteTradePositionSagPayload {
    payload: {
        takeProfit: number | undefined;
        stopLossActive: number;
        initialOrderStopLoss: {
            legs: { price: number }[],
            orderCode: IOrderCode,
        }
        stopLoss: number | undefined;
        symbol: ILiteTradeAssetSymbol,
        orderCode: IOrderCode,
        quantity: number,
        side: 'buy' | 'sell',
        orderType: ILiteTradePortfolioPositionType,
        limitPrice: number,
        stopPrice: number,
        takeProfitActive: boolean,
        initialOrderTakeProfit?: {
            legs: { price: number }[],
            orderCode: IOrderCode
        },
        initialQuantity: number,
        positionCode: IPositionCode,
        onSuccess?: () => void,
    };
}

export function* updateLiteTradePositionSaga({ payload }: IUpdateLiteTradePositionSagPayload) {
    try {
        const {
            initialOrderStopLoss,
            initialOrderTakeProfit,
            initialQuantity,
            positionCode,
            quantity,
            side,
            stopLoss,
            stopLossActive,
            symbol,
            takeProfit,
            takeProfitActive,
        } = payload;

        const orderSide = side.toLowerCase() === 'sell' ?
            LITE_TRADE_POSITION_SIDE.BUY :
            LITE_TRADE_POSITION_SIDE.SELL;
        const positionQuantityToClose = initialQuantity - quantity;
        if (positionQuantityToClose) {
            yield call<typeof closeLiteTradePositionSaga>(closeLiteTradePositionSaga, {
                payload: {
                    positionCode,
                    quantity: positionQuantityToClose,
                    side: orderSide,
                    symbol,
                },
                type: 'CLOSE_LITE_TRADE_POSITION_ACTION',
            });
        }

        const newOrderCode: string = yield call(getOrderCode);
        const selectedAccount: IAccountCode = yield select(getUserSelectedAccountCode);
        const ordersToCreate: ILiteTradePortfolioPosition[] = [];
        const ordersToUpdate: ILiteTradePortfolioPosition[] = [];
        const ordersToDelete: ILiteTradePortfolioPosition[] = [];
        const orderCommonParams = {
            positionCode,
            instrument: symbol,
            positionEffect: LITE_TRADE_POSITION_EFFECTS.CLOSE,
            side: orderSide,
            tif: 'GTC' as ILiteTradePortfolioPositionTift,
        };

        let newOrderTakeProfit = {
            ...orderCommonParams,
            orderCode: initialOrderTakeProfit?.orderCode || `${newOrderCode}-take` as IOrderCode,
            limitPrice: takeProfit,
            type: ORDER_TYPES.LIMIT,
        };

        let newOrderStopLoss = {
            ...orderCommonParams,
            orderCode: initialOrderStopLoss?.orderCode || `${newOrderCode}-stop` as IOrderCode,
            stopPrice: stopLoss,
            type: ORDER_TYPES.STOP,
        };

        [
            {
                initialOrder: initialOrderTakeProfit,
                newOrder: newOrderTakeProfit,
                isOrderActive: takeProfitActive,
                orderPrice: takeProfit,
            },
            {
                initialOrder: initialOrderStopLoss,
                newOrder: newOrderStopLoss,
                isOrderActive: stopLossActive,
                orderPrice: stopLoss,
            },
        ].forEach(({
                       initialOrder, newOrder, isOrderActive, orderPrice,
                   }) => {
            if (isOrderActive) {
                if (!initialOrder) {
                    ordersToCreate.push(newOrder);

                } else if (initialOrder.legs[0].price !== orderPrice) {
                    ordersToUpdate.push(_.omit(newOrder, ['type']));
                }
            } else if (initialOrder) {
                ordersToDelete.push(newOrder);
            }
        });

        yield all(ordersToDelete.map(orderBody =>
            call(deleteLiteTradeRequest, `/accounts/default:${encodeURIComponent(selectedAccount)}/orders/${encodeURIComponent(orderBody.orderCode)}`)),
        );
        yield all(ordersToUpdate.map(orderBody =>
            call(putLiteTradeRequest, `/accounts/default:${encodeURIComponent(selectedAccount)}/orders`, orderBody)),
        );
        yield all(ordersToCreate.map(orderBody =>
            call(postLiteTradeRequest, `/accounts/default:${encodeURIComponent(selectedAccount)}/orders`, orderBody)),
        );

        yield put(updateLiteTradePositionSuccess());
        yield call(payload.onSuccess);
    } catch (e) {
        yield call(failurePut, e, updateLiteTradePositionFailure);
    }
}

export function* loadLiteTradeMarketData({ payload }) {
    const { accountCode, symbols } = payload;
    const params = {
        account: `default:${accountCode}`,
        symbols: symbols,
        eventTypes: [
            {
                type: 'Quote',
                format: 'COMPACT',
            },
        ],
    };
    try {
        return yield call(postLiteTradeRequest, '/marketdata', params);
    } catch (e) {
        return e;
    }
}

export function* openLiteTradeBasicInfoNotificationSaga({ payload }) {
    const {
        accountCode,
        autoClose = true,
        description,
        filledQuantity,
        notificationChildren,
        NotificationComponent = LiteTradeNotificationBasicInfo,
        notificationOptions = {},
        notificationStatus,
        notificationType,
        order,
        pl,
        price,
        quantity,
    } = payload;
    const positionEffect = order?.legs[0]?.positionEffect;
    const assets = yield select(getLiteTradeAssets);
    const asset = _.find(assets, { symbol: payload.order.instrument });
    const assetInstance = new Asset(asset, { assets, selectedAccountCode: accountCode });
    yield call(assetInstance.isReady.bind(assetInstance));
    //@ts-ignore
    const lotsAmount = quantity / assetInstance.getLotSize();
    const margin = assetInstance.lotsToAmount(
        lotsAmount,
        price,
        positionEffect === LITE_TRADE_POSITION_EFFECTS.CLOSE
            ? MAP_LITE_TRADE_POSITION_OPPOSITE_SIDE[order.side]
            : order.side,
        filledQuantity,
    );
    const accountType = parseAccountName(order.account)?.type;

    yield put(openNotificationAction({
        content: <NotificationComponent
            accountType={accountType}
            assetInstance={assetInstance}
            description={description}
            lotsAmount={lotsAmount}
            margin={margin}
            order={order}
            pl={pl}
            price={price}
            status={notificationStatus}
            symbol={order.instrument}
            type={notificationType}
        >{notificationChildren}</NotificationComponent>,
        options: {
            autoClose,
            closeButton: LiteTradeNotificationCloseButton,
            ...notificationOptions,
        },
    }));
}

export function* processLiteTradePositionNotificationSaga({
                                                              payload,
                                                          }: ReturnType<typeof processLiteTradePositionNotificationAction>) {
    const { portfolio, order, filledQuantity } = payload;
    const positionEffect = order?.legs[0]?.positionEffect;
    const positionCode = order?.legs[0]?.positionCode;
    const position = _.find(portfolio.positions, { positionCode }) as ILiteTradePortfolioPosition;
    const notificationType: TLiteTradeNotificationType =
        positionEffect === LITE_TRADE_POSITION_EFFECTS.CLOSE
            ? position.quantity
                ? LITE_TRADE_NOTIFICATION_TYPES.POSITION_PARTIALLY_CLOSED
                : LITE_TRADE_NOTIFICATION_TYPES.POSITION_CLOSED
            : LITE_TRADE_NOTIFICATION_TYPES.POSITION_OPENED;
    const account = parseAccountName(order.account);
    const accountCode = account?.accountCode;
    const accountType = account?.type;
    const execution = order.executions.find((execution) => execution.status === order.status);
    const price = execution?.lastPrice;
    const quantity = Math.abs(execution?.lastQuantity);
    const pl = order.cashTransactions[0]?.value;
    const isCloseAll = yield select(getCloseAllPositionsIsLoading);
    const isClose = ([
        LITE_TRADE_NOTIFICATION_TYPES.POSITION_CLOSED,
        LITE_TRADE_NOTIFICATION_TYPES.POSITION_PARTIALLY_CLOSED,
    ] as TLiteTradeNotificationType[]).includes(notificationType);
    if (isClose && isCloseAll) {
        // skip notification if close all positions is in progress
        return;
    }

    const isBottomNotification = isClose;
    const NotificationComponent = isBottomNotification ?
        LiteTradeNotificationPositionClosed :
        LiteTradeNotificationBasicInfo;
    const isDesktop = yield select(getIsLocationPlatformDesktopSelector);
    const notificationOptions = isBottomNotification
        ? {
            className: classNames('background-surface  notification-position-closed', {
                'notification-margin-16': !isDesktop,
            }),
            limit: 1,
            position: isDesktop ? 'top-center' : 'bottom-center',
        }
        : (isDesktop ? { position: 'top-right' } : {});

    if (
        isExistSearchParam('order-id', String(order.orderId)) &&
        isExistSearchParam('s', MOBILE_SLIDER_PAGES_IDS.ORDER_UPDATE_2)
    ) {
        // if opened order edit for this orderId -> close it
        const searchParams = excludeSearchParams({
            s: MOBILE_SLIDER_PAGES_IDS.ORDER_UPDATE_2,
            'order-id': order.orderId as string,
        });
        appHistory.replace({
            search: searchParams.toString(),
        });
    }

    if (
        notificationType === LITE_TRADE_NOTIFICATION_TYPES.POSITION_CLOSED &&
        isExistSearchParam('position-code', positionCode) &&
        isExistSearchParam('s', MOBILE_SLIDER_PAGES_IDS.POSITION_UPDATE_2)
    ) {
        // if opened position edit for this positionCode -> close it
        const searchParams = excludeSearchParams({
            s: MOBILE_SLIDER_PAGES_IDS.POSITION_UPDATE_2,
            'position-code': positionCode,
        });
        appHistory.replace({
            search: searchParams.toString(),
        });
    }

    yield call(openLiteTradeBasicInfoNotificationSaga, {
        payload: {
            NotificationComponent,
            accountCode,
            filledQuantity,
            notificationOptions,
            notificationType,
            order,
            pl,
            price,
            quantity,
        },
    });

    if (accountType === ACCOUNT_TYPES.DEMO && notificationType === LITE_TRADE_NOTIFICATION_TYPES.POSITION_OPENED) {
        yield put(pushGtmEventAction({ event: GTM_EVENTS.POSITION_OPENED_DEMO }));
    }
}

export function* processLiteTradeOrderNotificationSaga({ payload }) {
    const { order, filledQuantity } = payload;
    const accountCode = parseAccountName(order.account)?.accountCode;
    // check initial get orders
    const currentAccountOrders = yield select(makeSelectorGetLiteTradeAccountOrdersByAccountCode(accountCode));
    if (!currentAccountOrders) {
        return;
    }

    const price = order?.legs[0]?.price;
    const quantity = order?.legs[0]?.quantity;
    const foundOrder = _.find(currentAccountOrders, { orderId: order.orderId });
    let isOrderChanged = false;
    if (order.status === LITE_TRADE_ORDER_STATUSES.WORKING && foundOrder) {
        const foundOrderQuantity = foundOrder.legs[0].quantity;
        const foundOrderPrice = foundOrder.legs[0].price;
        isOrderChanged = quantity !== foundOrderQuantity || price !== foundOrderPrice;
        if (!isOrderChanged) {
            return;
        }
    }

    const notificationType = isOrderChanged
        ? LITE_TRADE_NOTIFICATION_TYPES.ORDER_CHANGED
        : MAP_LITE_TRADE_ORDER_STATUS_TO_ORDER_NOTIFICATION_TYPE[order.status];

    const NotificationComponent = order.legs[0].positionEffect === LITE_TRADE_POSITION_EFFECTS.CLOSE
        ? LiteTradeNotificationProtectionOrderInfo
        : undefined;
    const isDesktop = yield select(getIsLocationPlatformDesktopSelector);
    yield call(openLiteTradeBasicInfoNotificationSaga, {
        payload: {
            NotificationComponent,
            accountCode,
            filledQuantity,
            notificationType,
            order,
            price,
            quantity,
            notificationOptions: isDesktop ? { position: 'top-right' } : undefined,
        },
    });
}

export function* processLiteTradeRejectedOrderNotificationSaga({ payload }) {
    const { description, notificationChildren, notificationStatus, order, autoClose = true, filledQuantity } = payload;
    const accountCode = parseAccountName(order.account)?.accountCode;
    const quantity = order?.legs[0]?.quantity;
    const notificationType = LITE_TRADE_NOTIFICATION_TYPES.ORDER_REJECTED;
    let price;
    if (order.type === ORDER_TYPES.MARKET) {
        const marketData = yield call(loadLiteTradeMarketData, {
            payload: {
                accountCode,
                symbols: [order.instrument],
            },
        });
        const event = marketData?.events[0] || {};
        price = order.side === LITE_TRADE_POSITION_SIDE.BUY ? event.ask : event.bid;
    } else {
        price = order?.legs[0]?.price;
    }
    const isDesktop = yield select(getIsLocationPlatformDesktopSelector);
    const NotificationComponent = order.legs[0].positionEffect === LITE_TRADE_POSITION_EFFECTS.CLOSE
        ? LiteTradeNotificationProtectionOrderInfo
        : undefined;

    yield call(openLiteTradeBasicInfoNotificationSaga, {
        payload: {
            NotificationComponent,
            accountCode,
            autoClose,
            description,
            filledQuantity,
            notificationChildren,
            notificationStatus,
            notificationType,
            order,
            price,
            quantity,
            notificationOptions: isDesktop ? { position: 'top-right' } : undefined,
        },
    });
}


export function* getLiteTradeAccountStatPLSaga({ payload }) {
    try {
        const selectedAccount: IAccountCode = yield select(getUserSelectedAccountCode);
        const data = yield call(getRequest, `/account/${encodeURIComponent(selectedAccount)}/pl/${payload}`);
        yield put(getLiteTradeAccountStatPLSuccess(data));
    } catch (e) {
        yield call(failurePut, e, getLiteTradeAccountStatPLFailure);
    }
}

export function* getLiteTradeMarketSaga() {
    try {
        const data = yield call(getRequest, '/market');
        yield put(getLiteTradeMarketSuccess(data));
    } catch (e) {
        yield call(failurePut, e, getLiteTradeMarketFailure);
    }
}

export function* getLiteTradeMarketCollectionSaga({ payload }) {
    try {
        const collection = yield call(getRequest, `/market/collection/${payload.collectionKey}`);
        yield put(getLiteTradeMarketCollectionSuccess(collection));
    } catch (e) {
        yield call(failurePut, e, getLiteTradeMarketCollectionFailure);
    }
}

export function* updateLiteTradeMarketViewAssetSaga({ payload }) {
    try {
        yield put(
            sendNotificationMessageAction({
                type: NOTIFICATIONS_MESSAGE_TYPES.MARKET,
                params: {
                    event: NOTIFICATION_MESSAGE_MARKET_EVENTS.VIEW_ASSET,
                    payload,
                },
            }),
        );
    } catch (e) {
        yield call(failurePut, e, updateLiteTradeViewAssetFailure);
    }
}

export function* updateLiteTradeMarketFavoriteAssetSaga({ payload }) {
    try {
        if (payload.active) {
            yield call(postRequest, `/market/favorites/${payload.symbol}`);
        } else {
            yield call<typeof deleteRequest>(deleteRequest, `/market/favorites/${payload.symbol}`);
        }
        yield put(updateLiteTradeMarketFavoriteAssetSuccess());
        yield put(getLiteTradeMarketCollectionAction({ collectionKey: COLLECTION_KEYS.FAVORITES }));
    } catch (e) {
        yield call(failurePut, e, updateLiteTradeMarketFavoriteAssetFailure);
    }
}

export function* initUpsDownsCollectionSaga({ payload }: ReturnType<typeof initUpsDownsCollectionAction>) {
    try {
        const data = yield call(getRequest, '/market/ups-downs');
        yield put(initUpsDownsCollectionSuccess(data));
    } catch (e) {
        yield call(failurePut, e, initUpsDownsCollectionFailure);
    }
}

export function* notifyOnCloseAllSaga({ payload }: { payload: { sumPl: number } }) {
    const isDesktop = yield select(getIsLocationPlatformDesktopSelector);
    yield put(openNotificationAction({
        content: <LiteTradeNotificationCloseAll sumPl={payload.sumPl}/>,
        options: {
            autoClose: true,
            className: classNames({
                'notification-margin-16': !isDesktop,
            }),
            closeButton: false,
            position: isDesktop ? 'top-center' : 'bottom-center',
        },
    }));
}

export function* closeAllPositionsSaga({ payload }: ReturnType<typeof closeAllPositionsAction>) {
    try {
        const selectedAccount: IAccountCode = yield select(getUserSelectedAccountCode);
        const data = yield call(postRequest, `/account/${encodeURIComponent(selectedAccount)}/positions/close-all`, { symbol: payload.symbol });
        yield put(closeAllPositionsSuccess(data));
        if (payload?.onSuccessCallback) {
            yield call(payload.onSuccessCallback);
        }
        yield call(notifyOnCloseAllSaga, { payload: { sumPl: payload.sumPl } });
    } catch (e) {
        yield call(failurePut, e, closeAllPositionsFailure);
    }
}

export function* updatePositionPlLastSignsByPortfolioSaga({ payload }: ReturnType<typeof updatePositionPlLastSeenSignByPortfolioAction>) {
    const accountCodePortfolio = parseAccountName(payload.account)?.accountCode;
    if (!accountCodePortfolio) {
        return;
    }
    const portfolioPositionsPlSeen = getLocalStorageItem(accountCodePortfolio, LOCAL_STORAGE_KEYS.PORTFOLIO_POSITIONS_PL_SEEN, {});

    yield put(updatePositionPlLastSeenSignByPortfolioSuccess({
        accountCode: accountCodePortfolio,
        data: {
            ..._.reduce(payload.positions, (acc, cur) => {
                acc[cur.positionCode] = portfolioPositionsPlSeen?.[cur.positionCode] || null;
                return acc;
            }, {}),
        },
    }));

    setLocalStorageItem(accountCodePortfolio, LOCAL_STORAGE_KEYS.PORTFOLIO_POSITIONS_PL_SEEN, {
        ..._.reduce(payload.positions, (acc, cur) => {
            acc[cur.positionCode] = portfolioPositionsPlSeen?.[cur.positionCode] || null;
            return acc;
        }, {}),
    });
}

export function updatePositionsPlLastSignByPositionCodeSaga({ payload }: ReturnType<typeof updatePositionPlLastSeenSignByPositionCodeAction>) {
    const currentPositionsPlSeenStorage = getLocalStorageItem(payload.accountCode, LOCAL_STORAGE_KEYS.PORTFOLIO_POSITIONS_PL_SEEN);
    setLocalStorageItem(payload.accountCode, LOCAL_STORAGE_KEYS.PORTFOLIO_POSITIONS_PL_SEEN, {
        ...currentPositionsPlSeenStorage,
        [payload.positionCode]: payload.sign,
    });
}

function* initChartDrawPanelOpenedSaga({ payload: isOpened }: ReturnType<typeof initChartDrawPanelOpenedAction>) {
    yield apply(localStorage, 'setItem', [TRADING_VIEW_STORAGE_KEY_CHART_DRAWING_TOOLBAR_VISIBLE, String(isOpened)]);
    yield put(setChartDrawPanelOpenedAction(isOpened));
}

export function* liteTradeSagas() {
    yield takeEvery(INIT_LITE_TRADE_ACTION, initLiteTradeSaga);
    yield takeEvery(PING_LITE_TRADE_ACTION, pingLiteTradeSaga);
    yield takeEvery(LOGIN_LITE_TRADE_ACTION, loginLiteTradeSaga);
    yield takeEvery(LOGOUT_LITE_TRADE_ACTION, logoutLiteTradeSaga);
    yield takeEvery(GET_LITE_TRADE_ASSETS_ACTION, getLiteTradeAssetsSaga);
    //@ts-ignore
    yield takeEvery(CREATE_LITE_TRADE_ORDER_ACTION, createLiteTradeOrderSaga);
    yield takeEvery(OPEN_LITE_TRADE_POSITION_ACTION, openLiteTradePositionSaga);
    yield takeEvery(CLOSE_LITE_TRADE_POSITION_ACTION, closeLiteTradePositionSaga);
    //@ts-ignore
    yield takeEvery(CANCEL_LITE_TRADE_ORDER_ACTION, cancelLiteTradeOrderSaga);
    yield takeEvery(PROCESS_LITE_TRADE_POSITION_NOTIFICATION, processLiteTradePositionNotificationSaga);
    //@ts-ignore
    yield takeEvery(PROCESS_LITE_TRADE_ORDER_NOTIFICATION, processLiteTradeOrderNotificationSaga);
    //@ts-ignore
    yield takeEvery(PROCESS_LITE_TRADE_REJECTED_ORDER_NOTIFICATION, processLiteTradeRejectedOrderNotificationSaga);
    yield takeEvery(UPDATE_LITE_TRADE_ORDER_ACTION, updateLiteTradeOrderSaga);
    //@ts-ignore
    yield takeEvery(UPDATE_LITE_TRADE_POSITION_ACTION, updateLiteTradePositionSaga);
    //@ts-ignore
    yield takeEvery(GET_LITE_TRADE_ACCOUNT_STAT_PL_ACTION, getLiteTradeAccountStatPLSaga);
    //@ts-ignore
    yield takeEvery(GET_LITE_TRADE_MARKET_ACTION, getLiteTradeMarketSaga);
    //@ts-ignore
    yield takeEvery(UPDATE_LITE_TRADE_MARKET_VIEW_ASSET_ACTION, updateLiteTradeMarketViewAssetSaga);
    //@ts-ignore
    yield takeEvery(GET_LITE_TRADE_MARKET_COLLECTION_ACTION, getLiteTradeMarketCollectionSaga);
    //@ts-ignore
    yield takeEvery(UPDATE_LITE_TRADE_MARKET_FAVORITE_ASSET_ACTION, updateLiteTradeMarketFavoriteAssetSaga);
    yield takeEvery(INIT_UPS_DOWNS_COLLECTION_ACTION, initUpsDownsCollectionSaga);
    yield takeEvery(CLOSE_ALL_POSITIONS_ACTION, closeAllPositionsSaga);
    yield takeEvery(UPDATE_POSITION_PL_LAST_SEEN_SIGN_BY_PORTFOLIO_ACTION, updatePositionPlLastSignsByPortfolioSaga);
    yield takeEvery(UPDATE_POSITION_PL_LAST_SEEN_SIGN_BY_POSITION_CODE_ACTION, updatePositionsPlLastSignByPositionCodeSaga);
    yield takeEvery(INIT_CHART_DRAW_PANEL_OPENED, initChartDrawPanelOpenedSaga);
}
